/**
 * Geocoding Service
 * Converts city names to coordinates using the Google Maps JavaScript API's
 * Geocoder, with caching to minimise API calls.
 *
 * NOT the REST geocoding web service (`maps.googleapis.com/maps/api/geocode`):
 * our browser key is HTTP-referrer-restricted, and that web service rejects
 * referrer-restricted keys outright —
 *   "API keys with referer restrictions cannot be used with this API."
 * Every lookup returned REQUEST_DENIED, which silently dropped any location
 * without stored coordinates from the map. The JS Geocoder honours referrer
 * restrictions, so it works with the key we already ship.
 */

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

// In-memory cache for geocoded results
const geocodeCache = new Map<string, GeocodingResult>();

/** The Maps JS API is loaded by whoever renders the map (useJsApiLoader). */
function getGeocoder(): google.maps.Geocoder | null {
  const maps = (window as any).google?.maps;
  if (!maps?.Geocoder) return null;
  return new maps.Geocoder();
}

/**
 * Geocode a city name to coordinates
 * @param city - City name (e.g., "Berlin")
 * @param countryName - Optional country name for better accuracy
 * @returns Coordinates for the city center, or null if geocoding failed
 */
export async function geocodeCity(
  city: string,
  countryName?: string
): Promise<GeocodingResult | null> {
  if (!city) return null;

  const cacheKey = `${city.toLowerCase()}|${(countryName || '').toLowerCase()}`;

  // Check cache first
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }

  const geocoder = getGeocoder();
  if (!geocoder) {
    console.warn('Geocoding: Maps JS API not loaded yet - skipping lookup');
    return null;
  }

  const query = countryName ? `${city}, ${countryName}` : city;

  try {
    const { results } = await geocoder.geocode({ address: query });
    const result = results?.[0];
    if (!result) {
      console.warn(`Geocoding: No results for "${query}"`);
      return null;
    }

    const geocodingResult: GeocodingResult = {
      latitude: result.geometry.location.lat(),
      longitude: result.geometry.location.lng(),
      formattedAddress: result.formatted_address,
    };

    geocodeCache.set(cacheKey, geocodingResult);
    return geocodingResult;
  } catch (error) {
    // ZERO_RESULTS surfaces as a rejection from the promise-based geocode().
    console.error(`Geocoding failed for "${query}":`, error);
    return null;
  }
}

/**
 * Clear the geocoding cache (useful for testing)
 */
export function clearGeocodeCache(): void {
  geocodeCache.clear();
}

/**
 * Get the current cache size
 */
export function getGeocacheCacheSize(): number {
  return geocodeCache.size;
}
